<template>
  <div class="monthly-item-calendar mt-4" id="printable-calendar">
    <div class="monthly-item-calendar__header">
      <h2 class="font-weight-bold calendar-heading d-inline-flex">{{ calendarItems.title }}</h2>
      <div class="calendar-action-buttons">
        <div class="pull-left">
          <h2 class="font-weight-bold calendar-month-year d-inline-flex">
            {{ currentMonthName }}<span>{{ selectedYear }}</span>
          </h2>
        </div>
        <b-button
          class="non-printable prev-button"
          @click="selectedMonthIncrementDecrement(false)"
          :disabled="calendarStartMonth === selectedMonth"
          variant="light"
          v-if="config.enablePrevButton && !(calendarStartMonth === calendarEndMonth)"
        >
          &#60;
        </b-button>
        <b-button
          class="non-printable next-button"
          @click="selectedMonthIncrementDecrement(true)"
          :disabled="calendarEndMonth === selectedMonth"
          variant="light"
          v-if="config.enableNextButton && !(calendarStartMonth === calendarEndMonth)"
        >
          &#62;
        </b-button>
        <button
          v-if="config.enablePrintCalendar"
          type="button"
          class="non-printable calendar-action-buttons__pdf"
          :disabled="isPdfBusy"
          @click="savePdf()"
        >
          {{ isPdfBusy ? 'PDF…' : 'PDF' }}
        </button>
        <div
          class="non-printable print-button"
          @click="printCalendar('printable-calendar')"
          v-if="config.enablePrintCalendar"
        >
          <i class="fa fa-print"></i>
        </div>
      </div>
    </div>

    <div class="monthly-item-calendar__scroll">
      <div class="monthly-item-calendar__grid">
        <div
          v-for="(day, index) in days"
          :key="'days' + index"
          class="monthly-item-calendar__dow calendar-days calendar-days-bottom"
          :class="'calendar-' + day.toLowerCase()"
        >
          {{ dayAbbrev(day) }}
        </div>

        <div
          v-for="index in emptyDays"
          :key="'emp' + index"
          class="monthly-item-calendar__cell monthly-item-calendar__cell--empty"
          :class="[
            'day-container-' + days[index - 1].toLowerCase(),
            { 'calendar-right-border': index % 7 === 0 },
          ]"
        >
          &nbsp;
        </div>

        <div
          v-for="(items, index) in filterData"
          :key="index"
          class="monthly-item-calendar__cell calendar calendar-body"
          :class="dayContainerClass(index)"
          @click="selectDay(index)"
        >
          <div class="calendar-date">{{ index + 1 }}</div>
          <div class="calendar-text">
            {{ items.title }}
            <span v-if="config.isShowMenuDescription" class="item-description">{{ items.description }}</span>
            <template v-if="items.choices && items.choices.length">
              <div
                :class="!items.title && index === 0 ? 'no-border' : ''"
                v-for="(item, choiceIndex) in items.choices"
                :key="choiceIndex"
              >
                {{ item.title }}<span class="item-price" v-if="config.isShowItemPrice"> {{ currency(item.price[0]) }}</span>
                <span v-if="config.isShowItemDescription" class="item-description">{{ item.description }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Moment from 'moment-timezone';
  import Currency from 'currency.js';
  import { calendarCellWeekdayName } from '@/utils/mealDayColors';
  import { buildMenuCalendarPdf } from '@/utils/calendarPdf';
  import { offerPdfDelivery, pdfDeliveryAlert, sanitizePdfFilename } from '@/utils/pdfDelivery';

  export default {
    name: 'orderMonthlyCalendar',
    props: {
      config: { type: Object, default: () => ({}) },
      calendarItems: { type: Object, default: () => ({}) },
    },
    data() {
      return {
        TZ: this.$root.account.tenant.TZ,
        selectedMonth: 1,
        selectedYear: 2020,
        days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        calendarEndMonth: Moment(this.calendarItems.calendarEndDate).tz(this.$root.account.tenant.TZ).month() + 1,
        calendarStartMonth: Moment(this.calendarItems.calendarStartDate).tz(this.$root.account.tenant.TZ).month() + 1,
        isPdfBusy: false,
      };
    },
    mounted() {
      this.selectedMonth = Moment(this.calendarItems.calendarStartDate).tz(this.$root.account.tenant.TZ).month() + 1;
      this.selectedYear = Moment(this.calendarItems.calendarStartDate).tz(this.$root.account.tenant.TZ).year();
    },
    computed: {
      filterData() {
        const tz = this.$root.account.tenant.TZ;
        const daysInMonth = Moment(`${this.selectedYear}-${this.selectedMonth}`, 'YYYY-MM')
          .tz(tz)
          .daysInMonth();
        const menuByDay = {};

        (this.calendarItems.menu || []).forEach((menuDay) => {
          if (!menuDay || !menuDay.menuDate) {
            return;
          }
          const menuMoment = Moment(menuDay.menuDate).tz(tz).add(12, 'hours');
          if (menuMoment.year() === this.selectedYear && menuMoment.month() + 1 === this.selectedMonth) {
            menuByDay[menuMoment.date()] = menuDay;
          }
        });

        const data = [];
        for (let day = 1; day <= daysInMonth; day++) {
          data.push(menuByDay[day] || {});
        }
        return data;
      },
      currentMonthName() {
        return Moment(`${this.selectedMonth}`, 'MM').tz(this.$root.account.tenant.TZ).format('MMMM');
      },
      monthTitle() {
        return `${this.currentMonthName} ${this.selectedYear}`;
      },
      emptyDays() {
        return Moment(`${this.selectedYear}-${this.selectedMonth}-01`, 'YYYY-MM-DD')
          .tz(this.$root.account.tenant.TZ)
          .day();
      },
    },
    methods: {
      currency(c) {
        return Currency(c);
      },
      dayAbbrev(dayName) {
        return String(dayName || '').slice(0, 3);
      },
      dayContainerClass(index) {
        const weekday = calendarCellWeekdayName(
          this.selectedYear,
          this.selectedMonth,
          index + 1,
          this.$root.account.tenant.TZ
        ).toLowerCase();
        let cls = 'day-container-' + weekday;
        if ((this.emptyDays + index + 1) % 7 === 0 || this.filterData.length === index + 1) {
          cls += ' calendar-right-border';
        }
        return cls;
      },
      selectedMonthIncrementDecrement(incrementDecrement) {
        if (incrementDecrement) {
          this.selectedMonth += 1;
        } else {
          this.selectedMonth -= 1;
        }
        if (this.selectedMonth === 13) {
          this.selectedMonth = 1;
          this.selectedYear += 1;
        }
        if (this.selectedMonth === 0) {
          this.selectedMonth = 12;
          this.selectedYear -= 1;
        }
        this.$emit('dateChanged', { year: this.selectedYear, month: this.selectedMonth });
      },
      selectDay(day) {
        this.$emit('click', { year: this.selectedYear, month: this.selectedMonth, day: day + 1 });
      },
      printCalendar() {
        this.$emit('print');
        window.print();
      },
      async savePdf() {
        if (this.isPdfBusy) {
          return;
        }
        this.isPdfBusy = true;
        try {
          const blob = buildMenuCalendarPdf({
            calendarTitle: this.calendarItems.title,
            monthTitle: this.monthTitle,
            emptyDays: this.emptyDays,
            dayMenus: this.filterData,
            year: this.selectedYear,
            month: this.selectedMonth,
            tz: this.$root.account.tenant.TZ,
            config: this.config,
            currency: this.currency,
            calendarCellWeekdayName,
          });
          const filename = sanitizePdfFilename(`${this.calendarItems.title}-${this.monthTitle}-menu`);
          const result = await offerPdfDelivery(blob, `${filename}.pdf`, this.$swal);
          const alert = pdfDeliveryAlert(result);
          if (alert) {
            await this.$swal.fire(alert);
          }
        } catch (err) {
          await this.$swal.fire({
            icon: 'error',
            title: 'Could not create PDF',
            text: (err && err.message) ? err.message : 'Please try again.',
          });
        } finally {
          this.isPdfBusy = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/sass/mealPalette';

  $mic-cell-min: 6.25rem;

  .monthly-item-calendar__header {
    margin-bottom: 0.5rem;
  }

  .monthly-item-calendar__scroll {
    width: 100%;
    max-width: 100%;
  }

  .monthly-item-calendar__grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    border: 1px solid #ccc;
    border-radius: 0.35rem;
  }

  .monthly-item-calendar__cell.calendar {
    display: flex;
    flex-direction: column;
    overflow: visible;
  }

  .monthly-item-calendar__dow {
    font-weight: 600;
    text-align: center;
    padding: 8px 0;
    border-right: 1px solid rgba(255, 255, 255, 0.25);
    border-bottom: 1px solid #ccc;

    &:nth-child(7n) {
      border-right: none;
    }
  }

  .monthly-item-calendar__cell {
    padding: 0 !important;
    border-left: 1px solid #ccc !important;
    border-bottom: 1px solid #ccc !important;
    min-height: 5.75rem;
    background: #fff;

    &--empty {
      background: #f4f4f4;
    }
  }

  .calendar {
    .calendar-date {
      float: none;
      flex-shrink: 0;
      font-size: 30px;
      padding: 10px 12px 0;
      line-height: 1;
    }

    .calendar-text {
      flex: 1 1 auto;
      padding: 0.35rem 10px 10px;
      text-align: right;
      font-size: 14px;
      min-width: 0;

      div {
        border-top: 1px solid #ddd;
      }

      .item-price {
        font-size: 0.7em;
      }

      .item-description {
        display: block;
        white-space: pre-wrap;
        font-size: 11px;
        color: #999;
      }

      .no-border {
        border: none;
      }
    }
  }

  .calendar-right-border {
    border-right: 1px solid #ccc;
  }

  .calendar-body {
    cursor: pointer;
  }

  .calendar-heading {
    font-size: 36px;
    color: $meal-brand;

    span {
      font-size: 24px;
    }
  }

  @include meal-calendar-day-headers;

  .calendar-action-buttons {
    float: right;

    button {
      background: $meal-brand !important;
      color: #fff !important;
    }

    .next-button {
      margin-right: 10px;
    }

    .pull-left {
      float: left;
    }

    &__pdf {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 4rem;
      margin-right: 0.65rem;
      padding: 0.35rem 0.85rem;
      border: none;
      border-radius: 2rem;
      background: darken($meal-brand, 8%) !important;
      color: #fff !important;
      font-size: 0.95rem;
      font-weight: 700;
      cursor: pointer;

      &:disabled {
        opacity: 0.65;
        cursor: wait;
      }
    }

    .print-button {
      font-size: 2em;
      float: right;
      margin-right: 1em;
      color: $meal-brand;
      cursor: pointer;
    }

    .calendar-month-year {
      font-size: 36px;
      color: $meal-brand;
      margin: 0;
      display: inline-flex;
      margin-bottom: 0;
      margin-right: 10px;

      span {
        font-size: 23px;
      }
    }
  }

  .calendar {
    @include meal-calendar-date-colors;
  }

  .calendar.day-container-sunday,
  .calendar.day-container-saturday {
    background: #f5f5f5;
  }

  @media (max-width: 991px) {
    .monthly-item-calendar__scroll {
      overflow: auto;
      -webkit-overflow-scrolling: touch;
      margin: 0 -0.15rem;
      padding: 0 0.15rem 0.15rem;
    }

    .monthly-item-calendar__grid {
      grid-template-columns: repeat(7, minmax($mic-cell-min, 1fr));
      min-width: calc(7 * #{$mic-cell-min});
      width: max-content;
      max-width: none;
    }

    .monthly-item-calendar__cell {
      min-width: $mic-cell-min;
    }
  }

  @media print {
    body {
      -webkit-print-color-adjust: exact important;
      margin: 0;
      padding: 0;
    }

    .non-printable,
    noprint,
    #nav {
      display: none;
    }

    .monthly-item-calendar__scroll {
      overflow: visible;
      margin: 0;
      padding: 0;
    }

    .monthly-item-calendar__grid {
      min-width: 0;
      width: 100%;
    }

    .calendar.day-container-sunday,
    .calendar.day-container-saturday {
      background: #fff;
    }
  }

  @media print and (width: 8.5in) and (height: 11in) {
    @page {
      margin: 0.25in;
    }
  }
</style>
